/**
 * StartPage - Orchestrator with Code Splitting
 * 
 * CHUNK ARCHITECTURE:
 * - Chunk A (Critical): Step1Page - loads immediately (~50KB)
 * - Chunk B (Lazy): QuizFlow - Steps 2, 3 - loads on first click (~80KB)
 * - Chunk C (Lazy): Results - loaded after Step 3 completion (~70KB)
 * 
 * This file acts as the orchestrator, deciding which chunk to show
 * based on user progress through the quiz.
 * 
 * Target: FCP < 800ms on 4G (vs 2.4s with monolithic bundle)
 */

import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import Step1Page from "./Step1Page";

// ============================================================================
// LAZY LOADED CHUNKS
// ============================================================================

// Chunk B: Steps 2 and 3 (loaded after Step 1 click)
const QuizFlow = lazy(() => import(/* webpackChunkName: "quiz-flow" */ "./StartPageFull"));

// ============================================================================
// LOADING FALLBACK (empty div - existing fade transition handles visual)
// ============================================================================

const LoadingFallback = () => (
  <div className="w-full max-w-lg transition-opacity duration-200 opacity-100" />
);

// ============================================================================
// META CAPI SDK INITIALIZATION
// ============================================================================

let capiParams = { fbc: '', fbp: '', clientIp: '' };

const initCapiParamBuilder = async () => {
  try {
    // Wait for SDK with timeout
    const waitForSdk = (timeout = 3000) => {
      return new Promise((resolve) => {
        if (typeof window.clientParamBuilder !== 'undefined') {
          resolve(window.clientParamBuilder);
          return;
        }
        const startTime = Date.now();
        const checkInterval = setInterval(() => {
          if (typeof window.clientParamBuilder !== 'undefined') {
            clearInterval(checkInterval);
            resolve(window.clientParamBuilder);
          } else if (Date.now() - startTime > timeout) {
            clearInterval(checkInterval);
            resolve(null);
          }
        }, 50);
      });
    };
    
    const sdk = await waitForSdk();
    if (sdk) {
      const getIpFn = async () => {
        try {
          const response = await fetch('https://api64.ipify.org?format=text');
          return await response.text();
        } catch { return ''; }
      };
      
      await sdk.processAndCollectAllParams(window.location.href, getIpFn);
      capiParams.fbc = sdk.getFbc() || '';
      capiParams.fbp = sdk.getFbp() || '';
      capiParams.clientIp = sdk.getClientIpAddress() || '';
    }
  } catch (error) {
    // Non-fatal error - CAPI params are optional for tracking enhancement
    // Log to console in development only, continue without CAPI params
    if (process.env.NODE_ENV === 'development') {
      console.warn('[CAPI] SDK initialization failed (non-fatal):', error.message);
    }
  }
};

// ============================================================================
// MAIN ORCHESTRATOR COMPONENT
// ============================================================================

export default function StartPage() {
  const [showStep1, setShowStep1] = useState(true);
  const [initialChoice, setInitialChoice] = useState(null);
  const [isLoadingChunk, setIsLoadingChunk] = useState(false);
  
  // Initialize CAPI SDK on mount (non-blocking)
  useEffect(() => {
    initCapiParamBuilder();
  }, []);
  
  // Handle Step 1 selection - triggers lazy load of QuizFlow
  const handleStep1Selection = useCallback((choice) => {
    setIsLoadingChunk(true);
    setInitialChoice(choice);
    
    // Use a small delay to show the transition, then switch to full app
    setTimeout(() => {
      setShowStep1(false);
    }, 150);
  }, []);
  
  // Step 1: Show critical path component (instant load)
  if (showStep1) {
    return (
      <Step1Page 
        onSelectOption={handleStep1Selection}
        isLoading={isLoadingChunk}
      />
    );
  }
  
  // Steps 2+: Show lazy-loaded full quiz flow
  return (
    <Suspense fallback={<LoadingFallback />}>
      <QuizFlow initialChoice={initialChoice} capiParams={capiParams} />
    </Suspense>
  );
}

// Export CAPI params for use in other components
export { capiParams };
